import api from "@/lib/api";
import { hasArea } from "@/lib/navigation";
import { loadCallMode } from "@/lib/routePreload";

const MODES = {
  cold: { area: "cold_call", target: 1 },
  personal: { area: "my_contacts", target: 10 },
  promo: { area: "promo", target: 10 },
};
const CACHE_MS = 8 * 60 * 1000;
const cache = new Map();

const keyFor = (userId, mode) => `${userId}:${mode}`;
const itemKey = (mode, item) => mode === "cold" ? item?.lead?.id : item?.customer?.id;
const constrainedConnection = () => {
  const connection = typeof navigator === "undefined" ? null : navigator.connection;
  return !!connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || "");
};

function releaseCold(entry) {
  const leadIds = entry?.items?.map(item => item?.lead?.id).filter(Boolean) || [];
  if (!leadIds.length) return Promise.resolve();
  return api.post("/call/batch/release", { lead_ids: leadIds }, { backgroundSafe: true }).catch(() => {});
}

function expire(key, entry) {
  if (cache.get(key) !== entry) return;
  cache.delete(key);
  if (entry.mode === "cold") releaseCold(entry);
}

function usableEntry(userId, mode) {
  const key = keyFor(userId, mode), entry = cache.get(key);
  if (!entry) return null;
  if (entry.expiresAt > Date.now()) return entry;
  expire(key, entry);
  return null;
}

function merge(entry, items) {
  const known = new Set(entry.items.map(item => itemKey(entry.mode, item)).filter(Boolean));
  for (const item of items || []) {
    const identity = itemKey(entry.mode, item);
    if (identity && !known.has(identity)) { known.add(identity); entry.items.push(item); }
  }
  entry.expiresAt = Date.now() + CACHE_MS;
  clearTimeout(entry.timer);
  entry.timer = setTimeout(() => expire(entry.key, entry), CACHE_MS);
  return entry.items;
}

export async function prefetchCallMode(user, mode, { full = false, userInitiated = false } = {}) {
  const config = MODES[mode];
  if (!user?.id || !config || !hasArea(user, config.area)) return [];
  const limited = constrainedConnection() && !userInitiated;
  const target = limited ? 1 : mode === "cold" && full ? 6 : config.target;
  const key = keyFor(user.id, mode);
  let entry = usableEntry(user.id, mode);
  if (!entry) {
    entry = { key, userId: user.id, mode, items: [], expiresAt: Date.now() + CACHE_MS, timer: null, promise: null };
    cache.set(key, entry);
  }
  if (entry.items.length >= target) return entry.items;
  if (entry.promise) return entry.promise.then(() => prefetchCallMode(user, mode, { full, userInitiated }));

  const excluded = entry.items.map(item => itemKey(mode, item)).filter(Boolean);
  let request;
  if (mode === "cold") {
    request = api.post("/call/batch", { limit: target, excluded_lead_ids: excluded }, { backgroundSafe: true });
  } else if (mode === "personal") {
    request = api.post("/my-contacts/batch", { limit: target - entry.items.length, excluded }, { backgroundSafe: true });
  } else {
    request = api.post("/promo/batch", { limit: target - entry.items.length, excluded_customer_ids: excluded }, { backgroundSafe: true });
  }
  entry.promise = request.then(({ data }) => merge(entry, data?.items)).catch(() => entry.items).finally(() => { entry.promise = null; });
  return entry.promise;
}

export async function warmCallModes(user, options = {}) {
  const full = !!options.full;
  const userInitiated = !!options.userInitiated;
  return Promise.allSettled([
    loadCallMode(),
    prefetchCallMode(user, "cold", { full, userInitiated }),
    prefetchCallMode(user, "personal", { full: true, userInitiated }),
    prefetchCallMode(user, "promo", { full: true, userInitiated }),
  ]);
}

export function scheduleCallPrefetch(user) {
  const run = () => warmCallModes(user).catch(() => {});
  if (typeof window !== "undefined" && "requestIdleCallback" in window) {
    return window.requestIdleCallback(run, { timeout: 1500 });
  }
  return setTimeout(run, 80);
}

export async function takeCallPrefetch(userId, mode) {
  let entry = usableEntry(userId, mode);
  if (!entry) return [];
  if (entry.promise) await entry.promise;
  entry = usableEntry(userId, mode);
  if (!entry) return [];
  cache.delete(entry.key); clearTimeout(entry.timer);
  return entry.items;
}

export async function releaseCallPrefetch(userId) {
  const entries = [...cache.values()].filter(entry => !userId || entry.userId === userId);
  const releases = [];
  for (const entry of entries) {
    cache.delete(entry.key); clearTimeout(entry.timer);
    if (entry.mode === "cold") releases.push(releaseCold(entry));
  }
  await Promise.allSettled(releases);
}

export function resetCallPrefetchForTests() {
  for (const entry of cache.values()) clearTimeout(entry.timer);
  cache.clear();
}
